import React, { useCallback, useEffect, useState } from 'react';
import tradesService from '../services/tradesService';

const EMPTY_FORM = {
  brokerage: '',
  accountName: '',
  accountNumber: '',
};

const safeTrim = (value) => String(value ?? '').trim();

export default function SavedAccountsModal({ show, onClose, onAccountsChanged }) {
  const [accounts, setAccounts] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingAccountId, setEditingAccountId] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deletingAccountId, setDeletingAccountId] = useState('');
  const [error, setError] = useState('');

  const loadAccounts = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await tradesService.getSavedAccounts();
      setAccounts(response.accounts || []);
    } catch (err) {
      console.error('Failed to load saved accounts:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to load accounts.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!show) return;
    setForm(EMPTY_FORM);
    setEditingAccountId('');
    loadAccounts();
  }, [loadAccounts, show]);

  if (!show) {
    return null;
  }

  const canSubmit = !saving &&
    safeTrim(form.brokerage) &&
    safeTrim(form.accountName) &&
    safeTrim(form.accountNumber);

  const handleFieldChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const startEditing = (account) => {
    setEditingAccountId(account.account_id);
    setForm({
      brokerage: account.brokerage,
      accountName: account.account_name,
      accountNumber: account.account_number,
    });
    setError('');
  };

  const cancelEditing = () => {
    setEditingAccountId('');
    setForm(EMPTY_FORM);
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;

    setSaving(true);
    setError('');
    try {
      const payload = {
        brokerage: safeTrim(form.brokerage),
        account_name: safeTrim(form.accountName),
        account_number: safeTrim(form.accountNumber),
      };
      if (editingAccountId) {
        await tradesService.updateSavedAccount(editingAccountId, payload);
      } else {
        await tradesService.createSavedAccount(payload);
      }
      setForm(EMPTY_FORM);
      setEditingAccountId('');
      await loadAccounts();
      if (onAccountsChanged) {
        await onAccountsChanged();
      }
    } catch (err) {
      console.error('Failed to save account:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to save account.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (account) => {
    const confirmed = window.confirm(`Delete saved account "${account.display_label}"?`);
    if (!confirmed) return;

    setDeletingAccountId(account.account_id);
    setError('');
    try {
      await tradesService.deleteSavedAccount(account.account_id);
      if (editingAccountId === account.account_id) {
        cancelEditing();
      }
      await loadAccounts();
      if (onAccountsChanged) {
        await onAccountsChanged();
      }
    } catch (err) {
      console.error('Failed to delete account:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to delete account.');
    } finally {
      setDeletingAccountId('');
    }
  };

  return (
    <>
      <div
        className="modal-backdrop fade show"
        style={{ zIndex: 1070 }}
        onClick={onClose}
      />
      <div
        className="modal fade show d-block"
        style={{ zIndex: 1075 }}
        tabIndex="-1"
        role="dialog"
        aria-modal="true"
        onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      >
        <div className="modal-dialog modal-lg modal-dialog-scrollable" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">
                <i className="bi bi-gear me-2"></i>
                Manage Saved Accounts
              </h5>
              <button
                type="button"
                className="btn-close"
                onClick={onClose}
                aria-label="Close"
              />
            </div>
            <div className="modal-body">
              {error && (
                <div className="alert alert-danger d-flex align-items-start gap-2">
                  <i className="bi bi-exclamation-triangle-fill mt-1"></i>
                  <span>{error}</span>
                </div>
              )}

              {loading ? (
                <div className="text-center py-3">
                  <span className="spinner-border spinner-border-sm me-2" aria-hidden="true"></span>
                  Loading accounts...
                </div>
              ) : (
                <div className="table-responsive mb-4">
                  <table className="table table-sm align-middle mb-0">
                    <thead>
                      <tr>
                        <th>Brokerage</th>
                        <th>Account Name</th>
                        <th>Account Number</th>
                        <th className="text-end">Trades</th>
                        <th className="text-end">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {accounts.length === 0 ? (
                        <tr>
                          <td colSpan="5" className="text-center text-muted">
                            No saved accounts yet. Add one below.
                          </td>
                        </tr>
                      ) : accounts.map((account) => (
                        <tr key={account.account_id}>
                          <td>{account.brokerage}</td>
                          <td>{account.account_name}</td>
                          <td>{account.account_number}</td>
                          <td className="text-end">{account.trade_count}</td>
                          <td className="text-end">
                            <div className="btn-group btn-group-sm">
                              <button
                                type="button"
                                className="btn btn-outline-secondary"
                                onClick={() => startEditing(account)}
                                disabled={saving || Boolean(deletingAccountId)}
                                title="Edit account"
                              >
                                <i className="bi bi-pencil"></i>
                              </button>
                              <button
                                type="button"
                                className="btn btn-outline-danger"
                                onClick={() => handleDelete(account)}
                                disabled={saving || Boolean(deletingAccountId)}
                                title={account.trade_count
                                  ? 'Accounts with trades cannot be deleted'
                                  : 'Delete account'}
                              >
                                {deletingAccountId === account.account_id ? (
                                  <span className="spinner-border spinner-border-sm" aria-hidden="true"></span>
                                ) : (
                                  <i className="bi bi-trash"></i>
                                )}
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <form onSubmit={handleSubmit}>
                <h6 className="fw-semibold">
                  {editingAccountId ? 'Edit Account' : 'Add Account'}
                </h6>
                <div className="row g-2">
                  <div className="col-md-4">
                    <label className="form-label fw-semibold">Brokerage</label>
                    <input
                      type="text"
                      className="form-control"
                      value={form.brokerage}
                      onChange={(e) => handleFieldChange('brokerage', e.target.value)}
                      placeholder="Schwab"
                      disabled={saving}
                      required
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label fw-semibold">Account Name</label>
                    <input
                      type="text"
                      className="form-control"
                      value={form.accountName}
                      onChange={(e) => handleFieldChange('accountName', e.target.value)}
                      placeholder="ROTH 401k"
                      disabled={saving}
                      required
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label fw-semibold">Account Number</label>
                    <input
                      type="text"
                      className="form-control"
                      value={form.accountNumber}
                      onChange={(e) => handleFieldChange('accountNumber', e.target.value)}
                      placeholder="1945-9312"
                      disabled={saving}
                      required
                    />
                  </div>
                </div>
                <div className="d-flex gap-2 mt-3">
                  <button type="submit" className="btn btn-primary" disabled={!canSubmit}>
                    {saving ? (
                      <span className="spinner-border spinner-border-sm me-2" aria-hidden="true"></span>
                    ) : (
                      <i className={`bi ${editingAccountId ? 'bi-save' : 'bi-plus-circle'} me-2`}></i>
                    )}
                    {editingAccountId ? 'Save Changes' : 'Add Account'}
                  </button>
                  {editingAccountId && (
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={cancelEditing}
                      disabled={saving}
                    >
                      Cancel
                    </button>
                  )}
                </div>
                {editingAccountId && (
                  <div className="form-text">
                    Renaming updates every trade that uses this account.
                  </div>
                )}
              </form>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={onClose}>
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
